import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Radio, ArrowRight, PhoneCall, MessageSquareText, FileCheck2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

// Monthly rotating hero: dark waves, fading to black where the text sits.
// Locked to the launch image until September 2026, then rotates by calendar month.
const LAUNCH_BG = "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/3419c176f670bf61b6eb30c63944b0d24a6ff5fd6ba44fa14e75c827f133667b.jpeg";
const MONTHLY_WAVES = [
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/db05af5fb0b3c1722a01f8e1f2ef9ea64dfa03590fbd78a86c39ab4472e7d976.jpeg", // Jan
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/6c83680179a8f61660384cf5de2f77593674083b5ab250eddb64aa081ced2b47.jpeg", // Feb
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/d8af4e8344d358bc7a3dc0fe0c77b56b9732b196f735d112c534192759615f29.jpeg", // Mar
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/c2f94d2adcfff3af6c807cd5032d13a95eb90beb03720243105a078bb370b176.jpeg", // Apr
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/2ca51b6a255ef935d11b5ad45e4ac40dcd8ad41f601194e5915f50e83de354a4.jpeg", // May
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/0883369710f35b390949b6f2dcb7ded5fcbbde2e8f4df6b5ffbb6f980f762939.jpeg", // Jun
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/821924f98b4e286557d250aa288c4391d111f71cf6db07e8fc7428e7ea1f759b.jpeg", // Jul
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/e2fe6321d10a92aae3aed4fbeaaf6ff779b905c6945763f9bac0ef9b254d5a04.jpeg", // Aug
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/925dbeaff07155cfa143a889646df026a677418c91026a5fe437081714dd4b36.jpeg", // Sep
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/fdbc00d0260867847eacca1fa1e1ea86e1dac0f03bfeb670fdd6b93a1a3a4d02.jpeg", // Oct
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/ac8f35dbd09998d44917b7952b9a1c1b3789705e9b8ca57852e389ed4214b89d.jpeg", // Nov
  "https://static.prod-images.emergentagent.com/jobs/959bc689-4b30-4d46-a7b6-d235dee99ca2/images/60be2be879651521b2be10f62a25d223e2cf12ae65d3ac79182240f6d9513347.jpeg", // Dec
];
const rotationStart = new Date(2026, 8, 1); // September 2026
const now = new Date();
const BG = now < rotationStart ? LAUNCH_BG : MONTHLY_WAVES[now.getMonth()];

export default function Landing() {
  const [signupsOpen, setSignupsOpen] = useState(true);

  useEffect(() => {
    api.get("/auth/config").then(({ data }) => setSignupsOpen(data.signups_open !== false)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* nav */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#007AFF] flex items-center justify-center">
              <Radio className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tighter">Incident Tracking &amp; Inquiry Services</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-signin-link">
              <Button variant="outline" className="rounded-sm border-border">Sign in</Button>
            </Link>
            {signupsOpen && (
              <Link to="/register" data-testid="landing-getstarted-link">
                <Button className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold gap-2">
                  Get started <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden border-b border-border">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-28 lg:py-36">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#007AFF] mb-5">Neutral incident intake, managed by AI</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-6" data-testid="landing-tagline">
            Every story.<br />Both sides.<br /><span className="text-[#007AFF]">On the record.</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl text-base md:text-lg leading-relaxed mb-9" data-testid="landing-intro">
            When things go wrong in an order, a delivery, a transaction, or with any product or service
            rendered insufficiently, inquiring minds want to know if the quality lapse was intentional or
            not. There are times in history where things have gone wrong on purpose — even with good
            intention, some planning and forethought. That conversation, though needed, can be difficult
            to have. We engage the vendor as a concerned third party, easily and calmly acting on your
            behalf — efficiently, without the drama.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            {signupsOpen ? (
              <>
                <Link to="/register" data-testid="landing-hero-cta">
                  <Button className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold gap-2 h-12 px-7 text-base">
                    Start keeping records <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/login" className="text-sm text-muted-foreground hover:text-white transition-colors duration-200">
                  Already have an account? Sign in
                </Link>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 border border-[#FFCC00]/40 bg-[#FFCC00]/10 text-[#FFCC00] rounded-sm px-4 py-3" data-testid="landing-private-notice">
                  <Lock className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-semibold">Private beta — new sign-ups are closed for now. Check back soon.</span>
                </div>
                <Link to="/login" data-testid="landing-hero-signin" className="text-sm text-muted-foreground hover:text-white transition-colors duration-200">
                  Have access? Sign in
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* proof strip: three-step flow */}
      <section className="border-b border-border bg-background" data-testid="landing-proof-strip">
        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
          {[
            {
              step: "01",
              icon: PhoneCall,
              title: "Report",
              copy: "Callers reach a calm AI line and describe what went wrong. Every detail is captured, structured, and timestamped.",
              testid: "proof-step-report",
            },
            {
              step: "02",
              icon: MessageSquareText,
              title: "Inquire",
              copy: "We engage the vendor as a neutral third party, drafting a measured inquiry on your behalf — no drama.",
              testid: "proof-step-inquire",
            },
            {
              step: "03",
              icon: FileCheck2,
              title: "Statement on record",
              copy: "Both sides are documented. Transcripts, audio, and the formal statement stay filed and downloadable.",
              testid: "proof-step-statement",
            },
          ].map(({ step, icon: Icon, title, copy, testid }) => (
            <div key={step} className="bg-background p-7 flex flex-col gap-4" data-testid={testid}>
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 bg-[#007AFF]/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#007AFF]" />
                </div>
                <span className="font-heading font-black text-2xl tracking-tighter text-muted-foreground/30">{step}</span>
              </div>
              <h3 className="font-heading font-black text-lg tracking-tight">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}
